/* eslint-disable no-constant-condition */
/* eslint-disable no-debugger */
import { useCallback, useEffect, useRef } from "react";
import config from "@Config/index";
import {
  calculateDistance,
  createCustomMarkerElement,
  getMapMarkerIconUrl,
  mapLoadedChina,
} from "../utils/utils";
import { useMapContext } from "../context/MapContext";
import { getRegions } from "services";
import useCookies from "hooks/useCookies";
import getRegion from "services/getRegions";
import axios from "axios";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, PAGE } from "constants/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useLazyCallback } from "hooks/useLazyCallback";

declare let mapboxgl: any;

interface UseMapboxProps {
  inventory: any;
  markerSize: number;
  propertyMarkerSize?: number;
  isMobile?: boolean;
  containerRef?: any;
}

// ---- Route config ---------------------------------------------------------
// Colors match the dots under the duration text in the distance card.
// Transit has no Mapbox profile — we fall back to driving and label it transit,
// or you can wire it to Google Directions if you need true transit routing.
type RouteMode = "walking" | "transit" | "driving";

const ROUTE_STYLES: Record<RouteMode, { color: string; profile: string; lineWidth: number }> = {
  driving: { color: "#ffe159ff", profile: "driving-traffic", lineWidth: 4 }, // blue
  transit: { color: "#10B981", profile: "driving", lineWidth: 4 }, // red (driving as proxy)
  walking: { color: "#3B82F6", profile: "walking", lineWidth: 4 }, // green
};

const ROUTE_SOURCE_PREFIX = "route-source-";
const ROUTE_LAYER_PREFIX = "route-layer-";
// ---------------------------------------------------------------------------

const useMapbox = ({
  inventory,
  markerSize,
  propertyMarkerSize,
  isMobile = false,
  containerRef,
}: UseMapboxProps) => {
  const { state, dispatch } = useMapContext();
  const {
    isMapScriptLoaded,
    mapInstance,
    mapCenter,
    isMapInitialized,
    isMapBuilt,
    inventoryMarker,
    nearbyLocationMarkers,
    activeTab,
    universities,
    showDistanceCard,
    showModal,
  } = state;

  const showRoutes = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_MAP_ROUTES_UK_INTERNATIONAL,
  );
  const secondaryMapBoxToken = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.USE_SECONDARY_TOKEN_MAPBOX_FOR_DISTANCE_API,
  );

  const searchRegionId = useCookies("searchRegionId");
  const initialUniversityId = Number.isNaN(parseInt(searchRegionId, 10))
    ? -1
    : parseInt(searchRegionId, 10);
  // Opening the map modal loads Mapbox immediately. Scrolling to the section later
  // must not inject the script again — a second copy replaces `mapboxgl` and
  // `fitBounds` throws because LngLatBounds from the new copy fails `instanceof`.
  const mapboxLoadStarted = useRef(false);

  // In-memory route cache — avoids redundant network calls within the same session
  const routeCache = useRef<Map<string, { geometry: any; duration: number; distance: number }>>(
    new Map(),
  );

  const fetchNearbyLocations = useCallback(
    async ({ id }: { id: any }) =>
      axios.get(`${config.API_URL}/api/v0/inventories/${id}/nearby_locations`, {
        params: {
          location_types:
            "university,bus_station,train_station,supermarket,pharmacy,restaurant,hospital,police,shopping_mall",
          ...(config.IS_CHINA_APP ? { lang: "en" } : {}),
          limit: 10,
        },
      }),
    [],
  );

  // Memoize the getNearbyLocations function
  const getNearbyLocations = useCallback(
    async (id: any) => {
      try {
        dispatch({ type: "SET_LOADING", payload: true });
        const { data = [] } = await fetchNearbyLocations({ id });
        const tabsRaw = data?.data?.data || [];
        const tabsModified = tabsRaw.filter((tab: any) => tab?.items?.length > 0);
        dispatch({ type: "SET_TABS", payload: tabsModified });
        dispatch({ type: "SET_ACTIVE_TAB", payload: tabsModified[0] });
        dispatch({ type: "SET_LOADING", payload: false });
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: "Failed to fetch nearby locations" });
        dispatch({
          type: "SET_TABS",
          payload: [
            {
              name: "University",
              items: [],
            },
          ],
        });
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [dispatch, fetchNearbyLocations],
  );

  // ---- Route fetching & rendering -----------------------------------------

  /**
   * Fetches a route via the backend directions API.
   * Also maintains an in-memory cache to avoid redundant calls within the same session.
   * Returns GeoJSON LineString geometry or null on failure.
   */
  const fetchRoute = async (
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number },
    profile: string,
  ) => {
    if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng || !profile) {
      return null;
    }

    const cacheKey = `${origin.lat},${origin.lng}|${destination.lat},${destination.lng}|${profile}`;

    // Check in-memory cache first
    if (routeCache.current.has(cacheKey)) {
      const cached = routeCache.current.get(cacheKey);
      if (cached) return cached;
    }

    try {
      const originCoord = `${origin.lng},${origin.lat}`;
      const destCoord = `${destination.lng},${destination.lat}`;

      const { data: response } = await axios.get(`${config.API_URL}/api/v0/directions`, {
        params: {
          origin: originCoord,
          destination: destCoord,
          profile,
          use_secondary_key: secondaryMapBoxToken, // growthbook
        },
      });

      const routeData = response?.data;
      if (!routeData?.geometry) return null;

      const result = {
        geometry: routeData.geometry,
        duration: routeData.duration, // seconds
        distance: routeData.distance, // meters
      };

      // Store in in-memory cache
      routeCache.current.set(cacheKey, result);
      return result;
    } catch (err) {
      console.error(`Failed to fetch ${profile} route:`, err);
      return null;
    }
  };

  /**
   * Removes all route layers and sources from the map.
   * Must run before drawing new routes to avoid stacking.
   */
  const clearRoutes = () => {
    if (!mapInstance) return;

    (Object.keys(ROUTE_STYLES) as RouteMode[]).forEach((mode) => {
      const layerId = `${ROUTE_LAYER_PREFIX}${mode}`;
      const sourceId = `${ROUTE_SOURCE_PREFIX}${mode}`;

      if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
      if (mapInstance.getSource(sourceId)) mapInstance.removeSource(sourceId);
    });
  };

  /**
   * Draws a single route line on the map for a given mode.
   */
  const drawRoute = (mode: RouteMode, geometry: any) => {
    if (!mapInstance || !geometry) return;

    const sourceId = `${ROUTE_SOURCE_PREFIX}${mode}`;
    const layerId = `${ROUTE_LAYER_PREFIX}${mode}`;
    const { color, lineWidth } = ROUTE_STYLES[mode];

    mapInstance.addSource(sourceId, {
      type: "geojson",
      data: { type: "Feature", properties: {}, geometry },
    });

    mapInstance.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": color,
        "line-width": lineWidth,
        "line-opacity": 0.85,
      },
    });
  };

  /**
   * Fetches routes for all four modes in parallel and draws them.
   * Also fits the map to include all route geometry.
   */
  const renderAllRoutes = async (destination: { lat: number; lng: number }) => {
    if (
      !mapInstance ||
      !inventory?.location_coordinates?.lat ||
      !inventory?.location_coordinates?.lng
    ) {
      return;
    }

    const origin = {
      lat: inventory.location_coordinates.lat,
      lng: inventory.location_coordinates.lng,
    };

    clearRoutes();

    const modes = Object.keys(ROUTE_STYLES) as RouteMode[];
    const results = await Promise.all(
      modes.map((mode) => fetchRoute(origin, destination, ROUTE_STYLES[mode].profile)),
    );

    // Draw routes in a specific order so driving renders on top (or reverse if preferred)
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([origin.lng, origin.lat]);
    bounds.extend([destination.lng, destination.lat]);

    modes.forEach((mode, i) => {
      const result = results[i];
      if (result?.geometry) {
        drawRoute(mode, result.geometry);
        // Extend bounds to include every point on the route
        result.geometry.coordinates.forEach((coord: [number, number]) => {
          bounds.extend(coord);
        });
      }
    });

    const paddingValue = isMobile ? 60 : 120;
    mapInstance.fitBounds(bounds, { padding: paddingValue, duration: 600 });
  };

  // --------------------------------------------------------------------------

  const loadMapboxScripts = () => {
    if (mapboxLoadStarted.current || (typeof mapboxgl !== "undefined" && mapboxgl?.Map)) {
      if (typeof mapboxgl !== "undefined" && mapboxgl?.Map) {
        dispatch({ type: "SET_MAP_SCRIPT_LOADED", payload: true });
      }
      return;
    }
    mapboxLoadStarted.current = true;

    // Check for WebGL2 support
    const supportsWebGL2 = () => {
      try {
        const canvas = document.createElement("canvas");
        return !!(window.WebGL2RenderingContext && canvas.getContext("webgl2"));
      } catch (e) {
        return false;
      }
    };

    // Determine which version to load based on WebGL2 support
    const useWebGL2 = supportsWebGL2();
    const mapboxVersion = useWebGL2 ? "v3.1.2" : "v2.15.0"; // Use v2.15.0 for WebGL1
    const jsVersion = useWebGL2 ? "v3.0.0" : "v2.15.0";

    // Create and append the CSS link element
    const linkEl = document.createElement("link");
    linkEl.setAttribute(
      "href",
      `https://api.mapbox.com/mapbox-gl-js/${mapboxVersion}/mapbox-gl.css`,
    );
    linkEl.setAttribute("rel", "stylesheet");
    document.head.appendChild(linkEl);

    // Create and append the script element
    const scriptEl = document.createElement("script");
    scriptEl.setAttribute("src", `https://api.mapbox.com/mapbox-gl-js/${jsVersion}/mapbox-gl.js`);
    scriptEl.setAttribute("type", "text/javascript");
    scriptEl.setAttribute("defer", "true");
    scriptEl.nonce = (window as any)?.__INITIAL_STATE__?.appData?.nonce || undefined;
    document.head.appendChild(scriptEl);
    scriptEl.addEventListener("load", () => {
      dispatch({ type: "SET_MAP_SCRIPT_LOADED", payload: true });
      // initializeMap();
    });
  };

  // Initialize map
  const initializeMap = () => {
    if (!mapboxgl || isMapInitialized) return;
    mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN;
    if (
      mapboxgl.supported() &&
      inventory?.location_coordinates?.lat &&
      inventory?.location_coordinates?.lng
    ) {
      // Modal close unmounts the map node the previous instance was bound to.
      // Drop that instance and clear the page container before creating a new one.
      try {
        mapInstance?.remove();
      } catch {
        // The modal container is already gone.
      }
      const mapContainer = document.getElementById("map2");
      if (mapContainer) mapContainer.replaceChildren();

      const map = new mapboxgl.Map({
        container: "map2",
        center: [inventory?.location_coordinates?.lng, inventory?.location_coordinates?.lat],
        zoom: 8,
        minZoom: 7,
      });
      map.scrollZoom.disable();
      map.addControl(new mapboxgl.NavigationControl(), "bottom-left");
      const center = map.getCenter();
      new mapboxgl.Marker({
        element: createCustomMarkerElement(
          getMapMarkerIconUrl("Property"),
          inventory.name || "",
          propertyMarkerSize || markerSize,
        ),
      })
        .setLngLat(center)
        .addTo(map);
      dispatch({ type: "SET_MAP_CENTER", payload: center });
      dispatch({ type: "SET_MAP_INSTANCE", payload: map });
      dispatch({ type: "SET_MAP_INITIALIZED", payload: true });
      buildMap();
    }
  };

  // Build map with markers
  const buildMap = () => {
    if (!mapInstance || isMapBuilt) return;

    if (mapLoadedChina() && isMapScriptLoaded) {
      if (
        inventory.location_coordinates &&
        inventory.location_coordinates.lat &&
        inventory.location_coordinates.lng
      ) {
        const el = document.createElement("img");
        el.setAttribute("src", getMapMarkerIconUrl("Property"));
        const newMarker = new mapboxgl.Marker(el)
          .setLngLat([inventory.location_coordinates.lng, inventory.location_coordinates.lat])
          .addTo(mapInstance);
        dispatch({ type: "SET_INVENTORY_MARKER", payload: newMarker });
      }
    }
    dispatch({ type: "SET_MAP_BUILT", payload: true });
  };

  // Reset map markers
  const resetMapMarkers = () => {
    if (nearbyLocationMarkers && nearbyLocationMarkers.length > 0) {
      nearbyLocationMarkers.forEach((marker: any) => marker.remove());
    }
    dispatch({ type: "CLEAR_NEARBY_LOCATION_MARKERS" });
    // Also clear routes whenever markers reset — keeps map state consistent
    clearRoutes();
  };

  // Update nearby location markers
  const updateNearbyLocationMarkers = (
    selectedSection: any,
    isDropdown = false,
    showLabelOnTop = false,
  ) => {
    // First reset existing markers
    resetMapMarkers();
    // gtagEventEmitter({
    //   action: "nearby_location",
    //   category: PAGE.INVENTORY,
    //   label: selectedSection?.name || undefined,
    // });
    const mapMarkerUrl = getMapMarkerIconUrl(
      isDropdown ? "Universities" : selectedSection?.name,
      selectedSection?.isSelected,
    );
    if (mapInstance) {
      const bounds = new mapboxgl.LngLatBounds();
      const centerLng = mapCenter?.lng;
      const centerLat = mapCenter?.lat;
      if (Number.isFinite(centerLng) && Number.isFinite(centerLat)) {
        bounds.extend([centerLng, centerLat]);
      }
      selectedSection?.items?.forEach((item: any) => {
        const lng = item?.lng ?? item?.location?.lng;
        const lat = item?.lat ?? item?.location?.lat;
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        const position = new mapboxgl.LngLat(lng, lat);
        const marker = new mapboxgl.Marker({
          element: createCustomMarkerElement(
            mapMarkerUrl,
            showLabelOnTop ? item.name || "" : "",
            markerSize,
          ),
        })
          .setLngLat(position)
          .addTo(mapInstance);
        // Add marker to state
        dispatch({ type: "ADD_NEARBY_LOCATION_MARKER", payload: marker });
        bounds.extend(position);
      });
      // Fit map to show all markers. An empty or invalid bounds throws inside
      // Mapbox and the error boundary unmounts the whole map section.
      const paddingValue = isMobile ? 50 : 150;
      if (typeof bounds.isEmpty === "function" && bounds.isEmpty()) return;
      try {
        mapInstance.fitBounds(bounds, {
          padding: paddingValue,
        });
      } catch (error) {
        console.error("Failed to fit map to nearby locations:", error);
      }
    }
  };

  // Fetch universities
  const fetchUniversities = async (searchName = "", forInitial = false) => {
    if (universities.length > 0) return;
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      if (searchName || forInitial) {
        const regionOptions = {
          url_path: "/api/v0/regions",
          params: {
            limit: 200,
            sort_key: "distance",
            sort_order: "asc",
            region_point_sort: [
              inventory?.location_coordinates?.lat,
              inventory?.location_coordinates?.lng,
            ].join(","),
            region_type: "establishment",
            region_inventory_id_point: inventory.id,
          } as any, // Use type assertion to allow dynamic properties
        };

        if (searchName) {
          regionOptions.params.name = searchName;
        }
        const { data } = await getRegions(regionOptions);

        if (!data || !data.data || !data.data.result || data.data.result.length === 0) {
          throw new Error("No universities found.");
        }

        let formatUnivData = [...data.data.result];
        const initialUniIndex = formatUnivData.findIndex(
          (uni: any) => uni.id == initialUniversityId,
        );

        if (initialUniversityId !== -1 && initialUniIndex === -1) {
          const res = await getRegion(`${initialUniversityId}`);
          if (res.data?.data.region_type === "establishment") {
            formatUnivData = [...formatUnivData, res?.data?.data];
          }
          // handleOptionClick(res.data?.data, true); // ToDo
        }

        // Format universities data to match the University interface
        const formattedUniversities = formatUnivData.map((uni: any) => ({
          id: uni.id.toString(),
          name: uni.name,
          location: uni.location,
        }));

        dispatch({ type: "SET_UNIVERSITIES", payload: formattedUniversities });
        dispatch({ type: "SET_OPTIONS", payload: formattedUniversities });

        // If this is a search (not initial load), update the markers
        if (searchName && mapInstance && initialUniversityId == -1) {
          // Reset existing markers first
          resetMapMarkers();

          // Create a section object for the universities
          const universitiesSection = {
            name: "Universities",
            items: formattedUniversities.map((uni: any) => ({
              name: uni.name,
              lat: uni.location.lat,
              lng: uni.location.lng,
            })),
          };

          // Update markers with the new universities
          updateNearbyLocationMarkers(universitiesSection, true, true);
        }
      }
    } catch (error) {
      console.error("Error fetching universities:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to fetch universities" });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  useEffect(() => {
    // Skip fetch if SSR already provided nearby locations (tabs are pre-populated from MapContext)
    if (state.tabs && state.tabs.length > 0) return;
    if (inventory?.id) {
      getNearbyLocations(inventory?.id);
    }
  }, [inventory?.id]);
  // Defer loading mapbox scripts until the section is scrolled into view.
  useLazyCallback(loadMapboxScripts, { rootMargin: "300px 0px 300px 0px" }, containerRef, []);

  // Force-load mapbox scripts immediately when modal is opened (bypasses IntersectionObserver)
  useEffect(() => {
    if (showModal && !isMapScriptLoaded) {
      loadMapboxScripts();
    }
  }, [showModal, isMapScriptLoaded]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      resetMapMarkers();
      if (mapInstance) {
        mapInstance.remove();
      }
    },
    [],
  );

  // Initialize map when script is loaded
  useEffect(() => {
    if (isMapScriptLoaded && !isMapInitialized) {
      initializeMap();
    }
  }, [isMapScriptLoaded, showModal]);

  // Build map when initialized
  useEffect(() => {
    if (isMapInitialized && !isMapBuilt) {
      buildMap();
    }
  }, [isMapInitialized]);

  // Fetch universities when map is built
  useEffect(() => {
    if (isMapBuilt && inventory?.id) {
      fetchUniversities("", true);
    }
  }, [isMapBuilt, inventory?.id]);

  useEffect(() => {
    if (
      isMapBuilt &&
      activeTab !== null &&
      activeTab?.items &&
      activeTab?.items?.length > 0 &&
      universities?.length > 0
    ) {
      dispatch({ type: "SET_SELECTED_OPTION", payload: null });
      const initialUniIndex = universities.findIndex((uni: any) => uni.id == initialUniversityId);
      if (
        initialUniversityId !== -1 &&
        initialUniIndex !== -1 &&
        activeTab?.name === "Universities"
      ) {
        dispatch({ type: "SET_VALUE", payload: universities[initialUniIndex].name || "" });
        handleOptionClick(universities[initialUniIndex], true);
      } else {
        dispatch({ type: "SET_VALUE", payload: "" });
        dispatch({ type: "SET_SHOW_DISTANCE_CARD", payload: false });
        updateNearbyLocationMarkers(activeTab, false, false);
      }
    }
  }, [activeTab, isMapBuilt, JSON.stringify(universities)]);

  // Reset routes when the distance card closes
  useEffect(() => {
    if (!showDistanceCard) {
      clearRoutes();
      updateNearbyLocationMarkers(activeTab, false, false);
    }
  }, [showDistanceCard]);

  const handleOptionClick = async (item: any, isDropdown = false) => {
    // Set the selected option in the state
    dispatch({ type: "SET_SELECTED_OPTION", payload: item });

    // Format item data for consistency
    const updatedItem = isDropdown
      ? {
          name: item?.combined_name || item?.name,
          id: item?.id,
          ...item?.location?.location_coordinates,
        }
      : {
          ...(item || {}),
          location: {
            location_coordinates: {
              lat: item?.lat,
              lng: item?.lng,
            },
          },
        };
    const destinationData = isDropdown ? item?.location?.location_coordinates : updatedItem;
    // Create an item object for the active tab
    const itemObj = {
      name: activeTab?.name || "",
      items: [updatedItem],
    };
    await calculateDistance(destinationData, inventory, (data) =>
      dispatch({ type: "SET_TRANSPORT_DATA_STRIP", payload: data }),
    );
    // Update the nearby location markers
    updateNearbyLocationMarkers(itemObj, isDropdown, true);

    // Draw routes from the property to the selected destination.
    // `destinationData` shape is { lat, lng } for both dropdown and tab clicks.
    if (destinationData?.lat && destinationData?.lng && showRoutes) {
      await renderAllRoutes({ lat: destinationData.lat, lng: destinationData.lng });
    }

    dispatch({ type: "SET_SHOW_DISTANCE_CARD", payload: true });
    gtagEventEmitter({
      action: isDropdown ? "university_selected_Map_section" : "map_tabs_option_clicked",
      category: PAGE.INVENTORY,
      label: isDropdown ? item?.combined_name || item?.name : activeTab?.name,
      value: !isDropdown ? item?.combined_name || item?.name : undefined,
    });
  };

  return {
    isMapScriptLoaded,
    map: mapInstance,
    center: mapCenter,
    inventoryMarker,
    nearbyLocationMarkers,
    createCustomMarkerElement,
    setMap: (map: any) => dispatch({ type: "SET_MAP_INSTANCE", payload: map }),
    setCenter: (center: any) => dispatch({ type: "SET_MAP_CENTER", payload: center }),
    initializeMap,
    buildMap,
    resetMapMarkers,
    isMapBuilt,
    updateNearbyLocationMarkers,
    handleOptionClick,
    fetchUniversities,
    // Exposed so the UI can clear routes on demand (e.g. closing distance card)
    clearRoutes,
    renderAllRoutes,
  };
};

export default useMapbox;
